import { useEffect, useContext, useCallback } from "react";
import { checkUserPermissions } from "authentication/ProtectedButton";
import { useAuth0 } from "@auth0/auth0-react";
import { getPermissions } from "api/PermissionsApi";
import { permissionsArrangement } from "layouts/pages/roles-and-permissions/components/DetailsView/tabs/Permissions";
import { PermissionsContext } from "context/AppProvider/PermissionsContext";
import { PERMISSIONS } from "constants/portal";
import { isStaleSessionError } from "helpers/authErrors";

export const usePermissions = () => {
  const { setPermissions, permissions, userPermissions, setUserPermissions } =
    useContext(PermissionsContext);
  const { user, getAccessTokenSilently, logout } = useAuth0();

  const fetchUserPermissions = async () => {
    if (!user) return;

    const userPermissionsResponse = await checkUserPermissions(
      user.stripeId,
      user.cxfUserId,
      Object.values(PERMISSIONS)
    );

    return userPermissionsResponse;
  };

  const fetchPermissions = async () => {
    if (!user) return;

    const fetchPermissionsResponse = await getPermissions(user.stripeId);
    const sorted = fetchPermissionsResponse?.sort((a, b) => {
      return permissionsArrangement[a.name] - permissionsArrangement[b.name];
    });

    return sorted;
  };

  const canViewAnyConnectors = useCallback(() => {
    return (
      userPermissions?.find((permission) => permission.name === PERMISSIONS.VIEW_ANY_CONNECTORS)
        ?.value || false
    );
  }, [userPermissions]);

  useEffect(() => {
    if (!user) return;

    const handleFetchingOfPermissions = async () => {
      if (!permissions) {
        try {
          const fetchedPermissions = await fetchPermissions();
          setPermissions(fetchedPermissions ?? []);
        } catch {
          setPermissions([]);
        }
      }

      if (!userPermissions) {
        try {
          await getAccessTokenSilently();
          const fetchedUserPermissions = await fetchUserPermissions();
          setUserPermissions(fetchedUserPermissions ?? []);
        } catch (error) {
          if (isStaleSessionError(error)) {
            logout({ logoutParams: { returnTo: window.location.origin + "/authentication/sign-in" } });
            return;
          }
          setUserPermissions([]);
        }
      }
    };

    handleFetchingOfPermissions();
  }, [user]);

  return {
    permissions,
    userPermissions,
    canViewAnyConnectors
  };
};
